'use client'
import { useEffect, useRef } from 'react'
import posthog from 'posthog-js'
import { useAuth } from '@/providers/auth-provider'
import { usePathname } from 'next/navigation'

// Auth pages where identify should not fire — the user state may be set here
// transiently during login before the redirect to the destination page completes.
// Firing identify on these pages causes PostHog to attribute the event to the
// wrong URL and prevents the anonymous pre-login person from being merged.
const AUTH_PATHS = [
  '/auth/signin',
  '/auth/signup',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/admin/login',
  '/admin/create-first-user',
  '/admin/forgot-password',
  '/admin/reset-password',
]

export function PostHogIdentifier() {
  const { user } = useAuth()
  const pathname = usePathname()
  const lastIdentifiedId = useRef<string | null>(null)

  useEffect(() => {
    // Skip identify while on auth pages — wait for the redirect to the destination
    if (AUTH_PATHS.some((p) => pathname?.startsWith(p))) return

    // Skip identify during impersonation — we don't want to pollute the impersonated user's profile
    const isImpersonating = document.cookie
      .split(';')
      .some((c) => c.trim().startsWith('impersonate='))
    if (isImpersonating) return

    if (user) {
      // Avoid redundant identify calls if the user hasn't changed
      if (lastIdentifiedId.current === user.id) return
      lastIdentifiedId.current = user.id
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        // isAdmin intentionally omitted — role flags should not be stored in third-party systems
      })
    } else {
      if (lastIdentifiedId.current !== null) {
        lastIdentifiedId.current = null
        posthog.reset()
      }
    }
  }, [user, pathname])

  return null
}
